import React, { useState, useEffect, useRef } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { useLanguage } from '../i18n/LanguageContext';
import { Activity, Trash2, Pause, Play } from 'lucide-react';

/**
 * Panel de eventos on-chain en tiempo real.
 * Escucha eventos Transfer, Approval, FaucetRequest, etc. del contrato ERC20.
 */
const EventsPanel = () => {
  const { web3, contract, contractAddress } = useWeb3();
  const { t } = useLanguage();
  const [events, setEvents] = useState([]);
  const [listening, setListening] = useState(true);
  const subscriptionRef = useRef(null);
  const lastSeenBlockRef = useRef(null);
  const maxEvents = 50;

  useEffect(() => {
    if (!contract || !web3 || !listening) return;

    const startListening = async () => {
      try {
        // Use polling for HTTP providers (Besu typically HTTP)
        const latestBlock = await web3.eth.getBlockNumber();
        const fromBlock = latestBlock > 100n ? latestBlock - 100n : 0n;

        // Fetch past events
        const pastTransfers = await contract.getPastEvents('Transfer', {
          fromBlock: Number(fromBlock),
          toBlock: 'latest',
        });

        const pastFaucet = await contract.getPastEvents('FaucetRequest', {
          fromBlock: Number(fromBlock),
          toBlock: 'latest',
        }).catch(() => []);

        const allPast = [...pastTransfers, ...pastFaucet]
          .sort((a, b) => Number(a.blockNumber) - Number(b.blockNumber))
          .slice(-maxEvents)
          .map(formatEvent);

        setEvents(allPast);
        // Guardar el último bloque visto para que el poll no pierda eventos
        lastSeenBlockRef.current = Number(latestBlock);
      } catch (err) {
        console.warn('Could not load past events:', err.message);
      }

      // Poll for new events every 5 seconds usando lastSeenBlock como fromBlock
      // para no perder eventos que caigan entre dos ciclos de polling
      const interval = setInterval(async () => {
        if (!listening) return;
        try {
          const latestBlock = Number(await web3.eth.getBlockNumber());
          const fromBlock = lastSeenBlockRef.current !== null
            ? lastSeenBlockRef.current
            : Math.max(0, latestBlock - 5);

          if (latestBlock < fromBlock) return;

          const newTransfers = await contract.getPastEvents('Transfer', {
            fromBlock,
            toBlock: 'latest',
          });

          const newFaucet = await contract.getPastEvents('FaucetRequest', {
            fromBlock,
            toBlock: 'latest',
          }).catch(() => []);

          lastSeenBlockRef.current = latestBlock + 1;

          const newEvents = [...newTransfers, ...newFaucet].map(formatEvent);

          setEvents((prev) => {
            const existingIds = new Set(prev.map((e) => e.id));
            const unique = newEvents.filter((e) => !existingIds.has(e.id));
            if (unique.length === 0) return prev;
            return [...prev, ...unique].slice(-maxEvents);
          });
        } catch (err) {
          // Silently ignore polling errors
        }
      }, 5000);

      subscriptionRef.current = interval;
    };

    startListening();

    return () => {
      if (subscriptionRef.current) {
        clearInterval(subscriptionRef.current);
      }
    };
  }, [contract, web3, listening]);

  const formatEvent = (event) => {
    const short = (addr) =>
      addr ? `${addr.substring(0, 6)}...${addr.substring(38)}` : '???';

    const id = `${event.transactionHash}-${event.logIndex}`;

    if (event.event === 'Transfer') {
      const from = event.returnValues.from;
      const to = event.returnValues.to;
      const value = web3.utils.fromWei(event.returnValues.value || '0', 'ether');
      const isMint = from === '0x0000000000000000000000000000000000000000';
      const isBurn = to === '0x0000000000000000000000000000000000000000';

      return {
        id,
        type: isMint ? 'Mint' : isBurn ? 'Burn' : 'Transfer',
        color: isMint ? 'text-green-400' : isBurn ? 'text-red-400' : 'text-blue-400',
        icon: isMint ? '🟢' : isBurn ? '🔴' : '🔵',
        message: isMint
          ? t('events.mintMsg', { value: parseFloat(value).toFixed(2), to: short(to) })
          : isBurn
          ? t('events.burnMsg', { value: parseFloat(value).toFixed(2), from: short(from) })
          : t('events.transferMsg', { value: parseFloat(value).toFixed(2), from: short(from), to: short(to) }),
        block: Number(event.blockNumber),
      };
    }

    if (event.event === 'FaucetRequest') {
      const amount = web3.utils.fromWei(event.returnValues.amount || '0', 'ether');
      return {
        id,
        type: 'Faucet',
        color: 'text-purple-400',
        icon: '💧',
        message: t('events.faucetMsg', { amount: parseFloat(amount).toFixed(2), to: short(event.returnValues.requester) }),
        block: Number(event.blockNumber),
      };
    }

    return {
      id,
      type: event.event || 'Unknown',
      color: 'text-gray-400',
      icon: '📡',
      message: t('events.unknownEvent', { event: event.event || 'Unknown', block: event.blockNumber }),
      block: Number(event.blockNumber),
    };
  };

  if (!contractAddress) return null;

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Activity className="w-6 h-6 text-purple-600" />
          <h2 className="text-2xl font-bold text-gray-800">{t('events.title')}</h2>
          {listening && (
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
            </span>
          )}
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setListening(!listening)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title={listening ? t('events.pause') : t('events.resume')}
          >
            {listening ? (
              <Pause className="w-4 h-4 text-gray-600" />
            ) : (
              <Play className="w-4 h-4 text-gray-600" />
            )}
          </button>
          <button
            onClick={() => setEvents([])}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title={t('events.clear')}
          >
            <Trash2 className="w-4 h-4 text-gray-600" />
          </button>
        </div>
      </div>

      <div className="bg-gray-900 rounded-lg p-4 max-h-64 overflow-y-auto font-mono text-sm">
        {events.length === 0 ? (
          <p className="text-gray-500 text-center py-4">
            {t('events.waiting')}
          </p>
        ) : (
          <div className="space-y-1">
            {events
              .slice()
              .reverse()
              .map((evt) => (
                <div key={evt.id} className="flex items-start space-x-2">
                  <span className="text-gray-500 flex-shrink-0">#{evt.block}</span>
                  <span>{evt.icon}</span>
                  <span className={evt.color}>{evt.message}</span>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsPanel;
